import type {
    CreatePoolDraft,
    Pool,
    PoolActionEligibility,
    PoolListModel,
    PoolPermission,
    PoolStatus,
    PoolStatusFilter,
    PoolView,
} from '.';
import { initialCreatePoolDraft, MIN_DELETE_POOL_REASON_LENGTH } from '.';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryClient, QueryObserverOptions } from '@tanstack/query-core';
import type { PermissionType } from '@thinkalpha/common/contracts/permissions.js';
import type { InventoryPool, ListPoolsResponse } from '@thinkalpha/ui-types/types/admin/lab-pools';
import { t } from 'i18next';
import { triggerCsvDownload } from 'src/admin/LAB/PoolManagement/csvDownload';
import { createPoolListCsv } from 'src/admin/LAB/PoolManagement/poolListCsv';
import type { PoolListCsvColumnLabels } from 'src/admin/LAB/PoolManagement/poolListCsv';
import type { InventoryRowsPerPage } from 'src/admin/models/PoolInventoryModel';
import type { PoolModel } from 'src/admin/models/PoolModel';
import { EVENT_EMAIL_PATTERN } from 'src/admin/models/PoolModel';
import { PoolModelImpl } from 'src/admin/models/PoolModel/impl';
import { inject, injectable } from 'src/features/ioc';
import { enableLabRevenueAnalytics } from 'src/lib/featureFlags/flags/enableLabRevenueAnalytics';
import { legacyLabPools } from 'src/lib/featureFlags/flags/legacyLabPools';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import {
    getAllPoolsQuery,
    getFirmPoolsQuery,
    invalidatePoolChangeLogQuery,
    invalidatePoolItemsQuery,
    invalidatePoolsQuery,
    LAB_POOLS,
} from 'src/queries/labPools';
import { createPool, deletePool, updatePool } from 'src/queries/labPoolsMutations';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Client-side shape check for the create-pool notification email; real
 * validation belongs to the backend, {@link EVENT_EMAIL_PATTERN} just
 * catches obvious typos before the POST. Shared with the Info tab's
 * edit flow so one field can't have two divergent validators — and it
 * deliberately matches the backend's own pattern rather than something
 * stricter, so a backend-accepted stored email never blocks edits.
 */

/**
 * The backend's `PermissionType` is the four-rung ladder
 * `reader | writer | owner | admin`. The pool UI only cares about
 * the read/write/own gating, so admin collapses to `owner`.
 */
function fromEffectiveAccess(access: PermissionType): PoolPermission {
    return access === 'admin' ? 'owner' : access;
}

/**
 * The single statement of the per-pool action policy: what the pool
 * cards, list, and Info tab may offer, and what the pause/resume and
 * delete commands enforce. Kept as one function so a policy change
 * cannot land on only one side.
 */
function derivePoolActionEligibility(pool: Pool, legacyPoolIds: ReadonlySet<string>): PoolActionEligibility {
    const isKnownStatus = pool.status === 'active' || pool.status === 'paused';
    return {
        isKnownStatus,
        canToggleStatus: pool.permission !== 'reader' && isKnownStatus,
        canDelete: pool.permission === 'owner' && !legacyPoolIds.has(pool.id),
    };
}

function fromApiPool(p: InventoryPool, statusOverride?: PoolStatus): Pool {
    return {
        id: p.id,
        name: p.name,
        description: p.description,
        firmId: p.firmId,
        eventEmail: p.eventEmail,
        providerIds: p.providerIds,
        permission: fromEffectiveAccess(p.permissions.effectiveAccess),
        // Backend exposes a boolean `active`; the UI surfaces a richer
        // `'active' | 'paused'` enum so future statuses (e.g. `draining`)
        // can land without re-keying templates. The override map only
        // wins while an optimistic toggle is in flight.
        status: statusOverride ?? (p.active ? 'active' : 'paused'),
        pricing: p.pricing ?? null,
    };
}

@injectable()
export class PoolListModelImpl implements PoolListModel {
    readonly #disposableStack = new DisposableStack();

    // --- View / selection / filters (plain signals; PLAT-13108 deleted the reducer) ---
    readonly #view = signal<PoolView>('list');
    readonly #selectedPoolId = signal<string | null>(null);
    readonly #poolSearchQuery = signal<string>('');
    readonly #poolStatusFilter = signal<PoolStatusFilter>('all');

    // --- Create Pool dialog lifecycle ---
    readonly #createPoolOpen = signal<boolean>(false);
    readonly #createPoolDraft = signal<CreatePoolDraft>(initialCreatePoolDraft);
    readonly #isCreatingPool = signal<boolean>(false);
    readonly #createPoolError = signal<string | null>(null);

    /**
     * Scrub the create-pool dialog lifecycle on navigation: the URL can
     * jump between views while the dialog is open (back/forward), and a
     * lingering `createPoolOpen` would unexpectedly re-open the modal on
     * the next return to the list. The draft is deliberately kept —
     * {@link openCreatePool} resets it.
     */
    #scrubCreatePoolLifecycle(): void {
        this.#createPoolOpen.value = false;
        this.#createPoolError.value = null;
        // `#isCreatingPool` deliberately survives navigation: the POST may
        // still be settling, and clearing the flag here would let a new
        // dialog fire a second create while the first is in flight (its
        // own settle handlers clear it).
    }

    /**
     * Firm scope. `undefined` = not yet initialized (queries disabled);
     * `null` = explicitly superuser scope (list-all); `string` = firm-scoped.
     */
    readonly #firmId = signal<string | null | undefined>(undefined);

    /**
     * `firmId → firmName` lookup, owned here so the search filter and
     * the detail view can read through it. `null` outside the superuser
     * view (where the firm-scoped page subtitle already names the firm).
     */
    readonly #firmNamesByFirmId = signal<Readonly<Record<string, string>> | null>(null);

    /**
     * Display name of the scoped firm in the firm-scoped view (where
     * {@link #firmNamesByFirmId} is null by design). Used so the CSV
     * export's firm column shows the name instead of the raw firm id.
     */
    readonly #firmName = signal<string | null>(null);

    /**
     * Pool-status overrides, keyed by pool id. Pool status is derived
     * from `InventoryPool.active`; this map holds short-lived UI
     * overrides during an optimistic Pause/Resume PATCH so the pill
     * never flashes back to the pre-toggle value between the request
     * and the cache patch. On success the entry is removed so the UI
     * falls back to the canonical `InventoryPool.active` (which the
     * success path also patches into the React-Query cache); on
     * failure the entry rolls back to the previous override. Survives
     * pool-switch but not page reloads.
     */
    readonly #poolStatusOverrides = signal<ReadonlyMap<string, PoolStatus>>(new Map());

    /**
     * Per-pool monotonic counter. Each `#setPoolStatus` call bumps the
     * version for `poolId`; success and error handlers only apply their
     * effects when their captured version still matches. Without this,
     * a fast pause → resume → pause sequence whose API calls settle out
     * of order can roll the optimistic state back to a stale value.
     */
    readonly #poolStatusMutationVersion = new Map<string, number>();

    /**
     * Pool currently awaiting a pause-confirmation. Non-null while the
     * confirm modal is open; the wizard clears it on cancel/confirm.
     */
    readonly #pendingPauseId = signal<string | null>(null);

    /**
     * Delete-pool dialog state. `#pendingDeleteId` is non-null while
     * the confirm dialog is open; the reason and error travel with it
     * so a failed DELETE keeps the user's typed justification for a
     * correct-and-retry loop.
     */
    readonly #pendingDeleteId = signal<string | null>(null);
    readonly #deletePoolReason = signal<string>('');
    readonly #isDeletingPool = signal<boolean>(false);
    readonly #deletePoolError = signal<string | null>(null);

    /**
     * Create-with-upload intent: id of a just-created pool whose detail
     * view should open with the upload wizard already up. Consumed by
     * the next {@link openPoolDetail} — after its OPEN_POOL_DETAIL
     * transition has scrubbed any stale wizard state — and dropped by
     * any other navigation. Plain field; nothing renders from it.
     */
    #pendingUploadWizardPoolId: string | null = null;

    /**
     * Wall-clock ms of the most recent point at which the open pool's
     * view became fresh: either a manual `refresh()` settled or a
     * successful mutation (submit, upload, pool status toggle,
     * permissions change) finished invalidating its queries. The
     * toolbar's refresh stamp reads through this so it ticks "just
     * now" automatically after every pool change.
     */
    readonly #lastRefreshAt = signal<number>(Date.now());

    /**
     * Discard-guard callbacks. `#pendingDiscard` holds the action that
     * runs when the user confirms; `#pendingDiscardCancel` (optional)
     * runs when they cancel. Callback-based rather than typed-intent so
     * the React shim's route blocker can park its `proceed` / `reset`
     * pair here without the model knowing about react-router.
     */
    readonly #pendingDiscard = signal<(() => void) | null>(null);
    readonly #pendingDiscardCancel = signal<(() => void) | null>(null);

    // --- Query observers ---
    readonly #poolsQuery: QueryObserverModel<ListPoolsResponse, Error, ListPoolsResponse>;
    readonly #queryClient: QueryClient;
    readonly #store: ReactBindings['Store'];
    readonly #currentUser: ReactBindings['CurrentUserModel'];
    readonly #activeUser: ReactBindings['ActiveUserModel'];
    readonly #legacyPoolIds: ReadonlySignal<string[]>;
    readonly canOpenRevenueAnalytics: ReadonlySignal<boolean>;
    /** Kept so the per-pool child models can construct their own query observers. */
    readonly #queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'];

    constructor(
        @inject('QueryObserverModelFactory')
        queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('QueryClient') queryClient: QueryClient,
        @inject('Store') store: ReactBindings['Store'],
        @inject('CurrentUserModel') currentUser: ReactBindings['CurrentUserModel'],
        @inject('FeatureFlagService') featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('ActiveUserModel') activeUser: ReactBindings['ActiveUserModel'],
    ) {
        this.#queryClient = queryClient;
        this.#store = store;
        this.#currentUser = currentUser;
        this.#activeUser = activeUser;
        this.#queryObserverModelFactory = queryObserverModelFactory;
        this.#legacyPoolIds = featureFlagService.getSignal(legacyLabPools);
        this.canOpenRevenueAnalytics = featureFlagService.getSignal(enableLabRevenueAnalytics);
        const poolsOptions = computed<QueryObserverOptions<ListPoolsResponse, Error, ListPoolsResponse>>(() => {
            const firmId = this.#firmId.value;
            // `null` is the explicit "superuser, list all" mode; `undefined`
            // means the page hasn't told us which firm yet, so disable the
            // firm-scoped query until it does.
            if (firmId === null) return getAllPoolsQuery();
            return getFirmPoolsQuery(firmId);
        });
        this.#poolsQuery = queryObserverModelFactory<ListPoolsResponse, Error, ListPoolsResponse>(poolsOptions);
        this.#disposableStack.use(this.#poolsQuery);
    }

    /**
     * Enqueue a persistent "in-flight" toast and return its id so the caller
     * can dismiss it once the action settles. Callers should follow up with
     * {@link #replaceToast} on both success and failure so the loader never
     * lingers after the work is done.
     */
    #startActionToast(message: string): string {
        const id = crypto.randomUUID();
        this.#store.dispatch({
            type: 'toast::enqueue',
            toast: {
                id,
                message,
                group: 'default',
                variant: 'info',
                autoHideDuration: null,
            },
        });
        return id;
    }

    #replaceToast(startId: string, finalToast: { message: string; variant: 'success' | 'error' }): void {
        this.#store.dispatch({ type: 'toast::dismiss', id: startId });
        this.#store.dispatch({
            type: 'toast::enqueue',
            toast: {
                message: finalToast.message,
                group: 'default',
                variant: finalToast.variant,
            },
        });
    }

    /**
     * Initializes the firm scope for this model. Pass `null` for the
     * superuser "list all pools" mode. The scope is part of the model's
     * identity: it is set once on a fresh instance, and the owning route
     * constructs a new model when the firm changes ({@link PoolManagement}
     * keys its disposable on `firmId`). Re-scoping a live instance throws.
     */
    setFirm(firmId: string | null): void {
        if (this.#firmId.value === firmId) return;
        if (this.#firmId.value !== undefined) {
            throw new Error(
                'PoolListModel firm scope is fixed after initialization; create a new instance for a different firm',
            );
        }
        // Drop the superuser-only firm-name map in firm scope so
        // `filteredPools` and `selectedPoolFirmName` can rely on
        // "map non-null ↔ superuser scope" regardless of call order.
        if (firmId !== null) this.#firmNamesByFirmId.value = null;
        this.#firmId.value = firmId;
    }

    setFirmNames(map: Readonly<Record<string, string>> | null): void {
        this.#firmNamesByFirmId.value = map;
    }

    setFirmName(name: string | null): void {
        this.#firmName.value = name;
    }

    // --- Loading / error signals ---
    readonly isLoading: ReadonlySignal<boolean> = computed(() => {
        if (this.#poolsQuery.signal.value.isLoading) return true;
        if (this.#poolModel.value?.inventoryModel.isLoading.value) return true;
        return false;
    });
    readonly error: ReadonlySignal<string | null> = computed(() => {
        const poolsError = this.#poolsQuery.signal.value.error;
        if (poolsError) return poolsError.message;
        return this.#poolModel.value?.inventoryModel.error.value ?? null;
    });

    // --- Pool list signals ---
    readonly pools: ReadonlySignal<ReadonlyArray<Pool>> = computed(() => {
        const overrides = this.#poolStatusOverrides.value;
        return this.#poolsQuery.signal.value.data?.pools.map((p) => fromApiPool(p, overrides.get(p.id))) ?? [];
    });
    readonly poolSearchQuery: ReadonlySignal<string> = computed(() => this.#poolSearchQuery.value);
    readonly poolStatusFilter: ReadonlySignal<PoolStatusFilter> = computed(() => this.#poolStatusFilter.value);
    readonly firmNamesByFirmId: ReadonlySignal<Readonly<Record<string, string>> | null> = computed(
        () => this.#firmNamesByFirmId.value,
    );
    readonly filteredPools: ReadonlySignal<ReadonlyArray<Pool>> = computed(() => {
        const query = this.#poolSearchQuery.value.trim().toLowerCase();
        const statusFilter = this.#poolStatusFilter.value;
        const all = this.pools.value;
        const firmNames = this.#firmNamesByFirmId.value;
        return all.filter((p) => {
            // Status filter is an AND gate ahead of the text search.
            if (statusFilter !== 'all' && p.status !== statusFilter) return false;
            if (!query) return true;
            if (p.name.toLowerCase().includes(query)) return true;
            if ((p.description ?? '').toLowerCase().includes(query)) return true;
            // Firm-name matching is superuser-only: the map is null in the
            // firm-scoped view (where every pool shares the same firm).
            if (firmNames !== null) {
                const firmName = firmNames[p.firmId];
                if (firmName && firmName.toLowerCase().includes(query)) return true;
            }
            return false;
        });
    });

    // --- View / selection signals ---
    readonly view: ReadonlySignal<PoolView> = computed(() => this.#view.value);
    readonly selectedPoolId: ReadonlySignal<string | null> = computed(() => this.#selectedPoolId.value);
    readonly #selectedPool: ReadonlySignal<Pool | null> = computed(() => {
        const id = this.#selectedPoolId.value;
        if (!id) return null;
        return this.pools.value.find((p) => p.id === id) ?? null;
    });
    readonly selectedPoolFirmName: ReadonlySignal<string | null> = computed(() => {
        const pool = this.#selectedPool.value;
        if (!pool) return null;
        const map = this.#firmNamesByFirmId.value;
        if (!map) return null;
        return map[pool.firmId] ?? null;
    });
    /**
     * Ids of legacy pools per the `legacy-lab-pools` feature flag, as a
     * set for per-pool gates in list contexts (e.g. hiding the pool
     * card's Delete action, which the backend rejects for legacy pools).
     */
    readonly legacyPoolIds: ReadonlySignal<ReadonlySet<string>> = computed(() => new Set(this.#legacyPoolIds.value));

    readonly poolActionEligibility: ReadonlySignal<ReadonlyMap<string, PoolActionEligibility>> = computed(() => {
        const legacy = this.legacyPoolIds.value;
        return new Map(this.pools.value.map((pool) => [pool.id, derivePoolActionEligibility(pool, legacy)]));
    });
    // --- Navigation commands ---
    openPoolList(): void {
        this.#runOrPromptDiscard(() => {
            this.#pendingUploadWizardPoolId = null;
            this.#teardownPoolModel();
            this.#view.value = 'list';
            this.#selectedPoolId.value = null;
            this.#scrubCreatePoolLifecycle();
        });
    }
    openPoolDetail(poolId: string): void {
        // A click on the already-open pool would otherwise burn an
        // unnecessary discard prompt; short-circuit when the target is
        // the current pool.
        if (this.#selectedPoolId.value === poolId && this.#view.value === 'detail') return;
        this.#runOrPromptDiscard(() => {
            // Drop the previous pool's model: staged inventory work, info
            // and pricing drafts, dialog state, and an abandoned upload
            // flow's parsed file must never resurface — or be applied —
            // on this pool. A fresh instance lands on the Info tab with
            // clean children.
            this.#teardownPoolModel();
            this.#view.value = 'detail';
            this.#selectedPoolId.value = poolId;
            this.#scrubCreatePoolLifecycle();
            this.#constructPoolModel(poolId);
            // Consume the create-with-upload intent: the teardown above
            // scrubbed any stale wizard, so this open always yields a
            // fresh 'drop'-step wizard. A mismatched pool id drops the
            // intent instead — the user navigated somewhere else first.
            const pendingUploadFor = this.#pendingUploadWizardPoolId;
            this.#pendingUploadWizardPoolId = null;
            if (pendingUploadFor === poolId) this.#poolModel.value?.inventoryModel.openUploadWizardForCreatedPool();
            // The pool-detail query observers refetch when `poolId`
            // changes, so the view is fresh as of right now — reset
            // the stamp so the toolbar doesn't read "Last refresh: 5
            // minutes ago" against data we just loaded.
            this.#markFresh();
        });
    }

    // --- Unsaved-changes guard ---
    readonly discardChangesOpen: ReadonlySignal<boolean> = computed(() => this.#pendingDiscard.value !== null);

    /**
     * Run `proceed` immediately when there are no unsaved edits.
     * Otherwise stash it behind the discard prompt: `confirmDiscardChanges`
     * runs it after wiping edits, `cancelDiscardChanges` runs `onCancel`
     * (if provided) so the caller can react to the user keeping their
     * work. Used internally by the navigation methods and externally by
     * the React shim's route blocker.
     */
    requestDiscardChanges(proceed: () => void, onCancel?: () => void): void {
        this.#runOrPromptDiscard(proceed, onCancel);
    }

    #runOrPromptDiscard(proceed: () => void, onCancel?: () => void): void {
        if (this.hasUnsavedChanges.value) {
            // Re-entrancy: a second navigation request can arrive while
            // a prompt is already open (e.g. the router blocker fires
            // again on a popstate). Fire the existing `onCancel` first
            // so any external blocker waiting on it (e.g. `blocker.reset`)
            // gets resolved before its callback is replaced.
            const previousCancel = this.#pendingDiscardCancel.value;
            this.#pendingDiscard.value = proceed;
            this.#pendingDiscardCancel.value = onCancel ?? null;
            previousCancel?.();
            return;
        }
        proceed();
    }

    confirmDiscardChanges(): void {
        const proceed = this.#pendingDiscard.value;
        if (!proceed) return;
        this.#pendingDiscard.value = null;
        // Confirm wins over cancel — drop the cancel callback so a
        // later `cancelDiscardChanges` (e.g. a delayed `blocker.reset`)
        // can't double-fire it.
        this.#pendingDiscardCancel.value = null;
        // Staged inventory work (edits, removals, pending adds, a typed
        // draft) lives on the per-pool inventory model; the navigation
        // that `proceed` triggers disposes that instance, so a confirmed
        // discard really discards.
        proceed();
    }

    cancelDiscardChanges(): void {
        if (this.#pendingDiscard.value === null) return;
        const onCancel = this.#pendingDiscardCancel.value;
        this.#pendingDiscard.value = null;
        this.#pendingDiscardCancel.value = null;
        onCancel?.();
    }

    // --- Pool detail tabs ---
    /**
     * Session-level superuser access. Derived directly from
     * `CurrentUserModel` so the model picks up superuser toggles
     * automatically without a separate React-side `setSuperuserEnabled`
     * round-trip. `isSuperuser` is the static "this user is allowed to
     * be a superuser" flag; `isSuperUserModeEnabled` is the time-bounded
     * session toggle the admin saga maintains (it clears when the access
     * window expires).
     */
    readonly #hasSuperuserSession: ReadonlySignal<boolean> = computed(
        () => this.#currentUser.isSuperuser.value && this.#currentUser.isSuperUserModeEnabled.value,
    );

    /**
     * Anything the discard prompts must protect, aggregated on the pool
     * model over its inventory / pricing / info children. `false` while
     * no pool is open — there is nothing to lose on the list view.
     */
    readonly hasUnsavedChanges: ReadonlySignal<boolean> = computed(
        () => this.#poolModel.value?.hasUnsavedChanges.value ?? false,
    );

    // --- Price summary dialog (PLAT-12938) ---
    readonly lastRefreshAt: ReadonlySignal<number> = computed(() => this.#lastRefreshAt.value);

    /**
     * Bump the refresh stamp to `now()`. Called from every successful
     * mutation path after its query invalidations settle, so the
     * toolbar's "Last refresh" label always reflects the moment the
     * pool view actually became fresh (manual refresh, submit, upload,
     * pool status toggle, permissions update).
     */
    #markFresh(): void {
        this.#lastRefreshAt.value = Date.now();
    }

    // --- Create pool ---
    readonly createPoolOpen: ReadonlySignal<boolean> = computed(() => this.#createPoolOpen.value);
    readonly createPoolDraft: ReadonlySignal<CreatePoolDraft> = computed(() => this.#createPoolDraft.value);
    readonly isCreatingPool: ReadonlySignal<boolean> = computed(() => this.#isCreatingPool.value);
    readonly createPoolError: ReadonlySignal<string | null> = computed(() => this.#createPoolError.value);
    /**
     * The firm selector only exists in the superuser "all firms" scope;
     * the firm-scoped page already fixes the target firm.
     */
    readonly createPoolNeedsFirm: ReadonlySignal<boolean> = computed(() => this.#firmId.value === null);
    readonly canSubmitCreatePool: ReadonlySignal<boolean> = computed(() => {
        const draft = this.#createPoolDraft.value;
        return (
            !this.#isCreatingPool.value &&
            draft.name.trim() !== '' &&
            EVENT_EMAIL_PATTERN.test(draft.eventEmail.trim()) &&
            (!this.createPoolNeedsFirm.value || draft.firmId !== '')
        );
    });

    openCreatePool(): void {
        // A create POST can still be settling after a navigation scrubbed
        // the dialog; block a new dialog until it resolves so its stale
        // settlement can't clear or fail a fresh draft.
        if (this.#isCreatingPool.value) return;
        // Opening always resets the draft so a cancelled form doesn't
        // leak into the next create.
        this.#createPoolOpen.value = true;
        this.#createPoolDraft.value = initialCreatePoolDraft;
        this.#isCreatingPool.value = false;
        this.#createPoolError.value = null;
    }

    closeCreatePool(): void {
        // Ignore close attempts while the POST is settling: the dialog
        // disables its close affordances, but a stray Escape or overlay
        // click shouldn't silently drop the in-flight lifecycle.
        if (this.#isCreatingPool.value) return;
        // The draft is deliberately kept — openCreatePool resets it, and
        // keeping it here avoids a flash of cleared fields while the
        // dialog animates out.
        this.#scrubCreatePoolLifecycle();
    }

    updateCreatePoolDraft(patch: Partial<CreatePoolDraft>): void {
        const draft = this.#createPoolDraft.value;
        const next = { ...draft, ...patch };
        if (
            next.name === draft.name &&
            next.description === draft.description &&
            next.eventEmail === draft.eventEmail &&
            next.firmId === draft.firmId &&
            next.initMode === draft.initMode
        ) {
            return;
        }
        this.#createPoolDraft.value = next;
    }

    /**
     * POST the current draft against `POST /v1/inventory/pools`. The
     * target firm is the page's firm scope, or the draft's `firmId` in
     * the superuser "all firms" view. On success: invalidate the pool
     * list (so the detail view can resolve the new pool), close the
     * dialog, toast, pre-open the upload wizard when the draft asked
     * for it (it only renders once the detail view mounts), and return
     * the new pool's id so the view can push the detail URL — the URL,
     * not the model, owns which pool is open. On failure the error
     * stays in the dialog for a correct-and-retry loop rather than a
     * transient toast.
     */
    async submitCreatePool(): Promise<string | null> {
        if (!this.canSubmitCreatePool.value) return null;
        const draft = this.#createPoolDraft.value;
        const scope = this.#firmId.value;
        const firmId = scope ?? draft.firmId;
        const name = draft.name.trim();
        // The submit gate already vetted the draft; this only guards the
        // uninitialized (`scope === undefined`) window before the page
        // sets a firm scope.
        if (!firmId) return null;
        this.#isCreatingPool.value = true;
        this.#createPoolError.value = null;
        try {
            const response = await createPool({
                name,
                description: draft.description.trim(),
                firmId,
                eventEmail: draft.eventEmail.trim(),
            });
            await invalidatePoolsQuery(this.#queryClient, scope ?? undefined);
            this.#markFresh();
            this.#createPoolOpen.value = false;
            this.#isCreatingPool.value = false;
            this.#createPoolError.value = null;
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t('admin::pool-management::toast-pool-created', { name }),
                    group: 'default',
                    variant: 'success',
                },
            });
            // Don't open the wizard here: the view still has to push the
            // detail URL, and OPEN_POOL_DETAIL scrubs upload state to kill
            // stale wizards from abandoned sessions. Park the intent
            // instead; `openPoolDetail` consumes it after its own
            // transition, so the new pool's wizard always starts from a
            // clean 'drop' step.
            if (draft.initMode === 'upload') this.#pendingUploadWizardPoolId = response.pool.id;
            return response.pool.id;
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            this.#isCreatingPool.value = false;
            this.#createPoolError.value = message;
            return null;
        }
    }

    readonly pendingPauseId: ReadonlySignal<string | null> = computed(() => this.#pendingPauseId.value);

    /**
     * Route a status toggle to pause-with-confirmation or instant
     * resume based on the pool's current status. Match each known
     * status explicitly: the `PoolStatus` union is intentionally
     * extensible (e.g. a future `'draining'`), and a generic `else`
     * resume would silently send the wrong mutation when a new state
     * lands, so this falls through to a no-op until it grows a branch.
     */
    requestTogglePoolStatus(poolId: string): void {
        const pool = this.pools.value.find((p) => p.id === poolId);
        if (!pool) return;
        if (pool.status === 'active') this.requestPausePool(poolId);
        else if (pool.status === 'paused') this.resumePool(poolId);
    }
    /**
     * Open the pause-confirmation modal for a pool. No-ops if the pool
     * is already paused or {@link poolActionEligibility} forbids the
     * toggle.
     */
    requestPausePool(poolId: string): void {
        const pool = this.pools.value.find((p) => p.id === poolId);
        if (!pool || pool.status !== 'active') return;
        if (!derivePoolActionEligibility(pool, this.legacyPoolIds.value).canToggleStatus) return;
        this.#pendingPauseId.value = poolId;
    }
    cancelPausePool(): void {
        this.#pendingPauseId.value = null;
    }
    /**
     * Commit the pending pause (called from the confirm modal). Drops
     * the pending id whether or not the toggle actually fires; the toast
     * surfaces the resulting state for visibility.
     */
    confirmPausePool(): void {
        const poolId = this.#pendingPauseId.value;
        this.#pendingPauseId.value = null;
        if (!poolId) return;
        this.#setPoolStatus(poolId, 'paused');
    }
    /**
     * Flip a paused pool back to active. No confirmation prompt — the
     * action is recoverable by pausing again.
     */
    resumePool(poolId: string): void {
        const pool = this.pools.value.find((p) => p.id === poolId);
        if (!pool || pool.status !== 'paused') return;
        if (!derivePoolActionEligibility(pool, this.legacyPoolIds.value).canToggleStatus) return;
        this.#setPoolStatus(poolId, 'active');
    }

    // --- Delete pool ---
    readonly pendingDeletePool: ReadonlySignal<Pool | null> = computed(() => {
        const id = this.#pendingDeleteId.value;
        if (!id) return null;
        return this.pools.value.find((p) => p.id === id) ?? null;
    });
    readonly deletePoolReason: ReadonlySignal<string> = computed(() => this.#deletePoolReason.value);
    readonly isDeletingPool: ReadonlySignal<boolean> = computed(() => this.#isDeletingPool.value);
    readonly deletePoolError: ReadonlySignal<string | null> = computed(() => this.#deletePoolError.value);
    readonly canConfirmDeletePool: ReadonlySignal<boolean> = computed(
        () =>
            this.pendingDeletePool.value !== null &&
            !this.#isDeletingPool.value &&
            this.#deletePoolReason.value.trim().length >= MIN_DELETE_POOL_REASON_LENGTH,
    );

    /**
     * Open the delete-confirm dialog for `poolId` with a blank reason.
     * Gated on {@link poolActionEligibility}: `owner` pool access — the
     * closest client-side proxy for the backend's firm-owner requirement
     * (firm-level grants surface in the pool's applied permissions; a
     * holder of a direct pool-only owner grant gets the backend's 403 in
     * the dialog) — on a modern pool, since the backend 400s deletes of
     * legacy pools (PLAT-12869).
     */
    requestDeletePool(poolId: string): void {
        const pool = this.pools.value.find((p) => p.id === poolId);
        if (!pool || !derivePoolActionEligibility(pool, this.legacyPoolIds.value).canDelete) return;
        // Mirror cancelDeletePool's guard: a new request must not hijack
        // the dialog state while a DELETE is still settling.
        if (this.#isDeletingPool.value) return;
        this.#pendingDeleteId.value = poolId;
        this.#deletePoolReason.value = '';
        this.#deletePoolError.value = null;
    }

    cancelDeletePool(): void {
        // Ignore close attempts while the DELETE is settling — a stray
        // Escape or overlay click shouldn't silently drop the in-flight
        // lifecycle.
        if (this.#isDeletingPool.value) return;
        this.#pendingDeleteId.value = null;
        this.#deletePoolReason.value = '';
        this.#deletePoolError.value = null;
    }

    setDeletePoolReason(reason: string): void {
        this.#deletePoolReason.value = reason;
    }

    /**
     * Issue `DELETE /v1/inventory/pools/{poolId}` with the trimmed
     * reason. On success: invalidate the pool list (dropping the card
     * from the list view), close the dialog, and toast. On failure the
     * error stays in the dialog for a correct-and-retry loop.
     */
    async confirmDeletePool(): Promise<boolean> {
        if (!this.canConfirmDeletePool.value) return false;
        const pool = this.pendingDeletePool.value;
        if (!pool) return false;
        const reason = this.#deletePoolReason.value.trim();
        this.#isDeletingPool.value = true;
        this.#deletePoolError.value = null;
        try {
            await deletePool({ poolId: pool.id, request: { reason } });
            await invalidatePoolsQuery(this.#queryClient, this.#firmId.value ?? undefined);
            this.#markFresh();
            this.#pendingDeleteId.value = null;
            this.#deletePoolReason.value = '';
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t('admin::pool-management::toast-pool-deleted', { name: pool.name }),
                    group: 'default',
                    variant: 'success',
                },
            });
            return true;
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            this.#deletePoolError.value = message;
            return false;
        } finally {
            this.#isDeletingPool.value = false;
        }
    }

    /**
     * Optimistically flip a pool's status, then call the backend. The
     * UI updates immediately so the pill / toast feel snappy; on
     * rejection we roll back the override and surface an error toast.
     *
     * Hits `PATCH /v1/inventory/pools/{poolId}` with `{ active }`. On
     * success we patch the pool-list cache with the response, then
     * clear the override entry entirely so the cache becomes the sole
     * source of truth — keeping a stale override would mask a later
     * server-side change (e.g. another admin pausing the pool). On
     * failure the override snaps back to whatever was there before.
     */
    #setPoolStatus(poolId: string, status: PoolStatus): void {
        const pool = this.pools.value.find((p) => p.id === poolId);
        if (!pool) return;
        // Centralized write-permission gate: callers can reach this path
        // after the pool's permission has dropped (the pause-confirm
        // modal can stay open while the user's access is revoked); fail
        // closed locally rather than relying on the server PATCH to
        // bounce.
        if (pool.permission === 'reader') return;
        if (pool.status === status) return;
        const previousOverride = this.#poolStatusOverrides.value.get(poolId);
        const optimistic = new Map<string, PoolStatus>(this.#poolStatusOverrides.value);
        optimistic.set(poolId, status);
        this.#poolStatusOverrides.value = optimistic;
        const version = (this.#poolStatusMutationVersion.get(poolId) ?? 0) + 1;
        this.#poolStatusMutationVersion.set(poolId, version);

        void updatePool({ poolId, request: { active: status === 'active' } }).then(
            (response) => {
                if (this.#poolStatusMutationVersion.get(poolId) !== version) return;
                // Patch the cached pool entries with the server's confirmed
                // `active` value, then drop the override so subsequent
                // pool-list refetches drive the pill directly. Touch both
                // the unscoped (`['lab-pools']`) and the firm-scoped
                // (`['lab-pools', firmId]`) caches so superuser and firm
                // views stay in sync.
                this.#patchPoolInCache(response.pool);
                const cleared = new Map<string, PoolStatus>(this.#poolStatusOverrides.value);
                cleared.delete(poolId);
                this.#poolStatusOverrides.value = cleared;
                this.#markFresh();
                const messageKey =
                    status === 'paused'
                        ? 'admin::pool-management::toast-pool-paused'
                        : 'admin::pool-management::toast-pool-resumed';
                this.#store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t(messageKey, { name: pool.name }),
                        group: 'default',
                        variant: 'success',
                    },
                });
            },
            (error: unknown) => {
                if (this.#poolStatusMutationVersion.get(poolId) !== version) return;
                // The captured `previousOverride` may not match the
                // server truth if earlier in-flight PATCHes for this
                // pool were version-gated out and never wrote it back.
                // Roll the optimistic entry back as a best-effort
                // visual restore, then invalidate the pool-list query
                // so the next refetch reseats `active` from the
                // backend and clears the lingering override.
                const reverted = new Map<string, PoolStatus>(this.#poolStatusOverrides.value);
                if (previousOverride === undefined) reverted.delete(poolId);
                else reverted.set(poolId, previousOverride);
                this.#poolStatusOverrides.value = reverted;
                void invalidatePoolsQuery(this.#queryClient, this.#firmId.value ?? undefined);
                const message = error instanceof Error ? error.message : String(error);
                const errorKey =
                    status === 'paused'
                        ? 'admin::pool-management::toast-pool-pause-error'
                        : 'admin::pool-management::toast-pool-resume-error';
                this.#store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t(errorKey, { name: pool.name, message }),
                        group: 'default',
                        variant: 'error',
                    },
                });
            },
        );
    }

    /**
     * Patch a single pool's entry inside every cached pool-list query
     * (both the unscoped `[LAB_POOLS]` superuser view and any
     * firm-scoped `[LAB_POOLS, firmId]` views). Used by `#setPoolStatus`
     * to fold the server's confirmed `active` value into the cache
     * without triggering a refetch round-trip.
     */
    #patchPoolInCache(updated: InventoryPool): void {
        const matches = this.#queryClient.getQueriesData<ListPoolsResponse>({ queryKey: [LAB_POOLS] });
        for (const [key, data] of matches) {
            if (!data) continue;
            if (!data.pools.some((p) => p.id === updated.id)) continue;
            this.#queryClient.setQueryData<ListPoolsResponse>(key, {
                ...data,
                pools: data.pools.map((p) => (p.id === updated.id ? updated : p)),
            });
        }
    }

    // --- Search commands ---
    setPoolSearchQuery(query: string): void {
        this.#poolSearchQuery.value = query;
    }
    setPoolStatusFilter(filter: PoolStatusFilter): void {
        this.#poolStatusFilter.value = filter;
    }
    // --- Per-pool model (one per open pool) ---
    readonly #poolModel = signal<PoolModelImpl | null>(null);
    readonly poolModel: ReadonlySignal<PoolModel | null> = computed(() => this.#poolModel.value);
    /**
     * Page-size preferences shared across pool switches (the page itself
     * resets with each per-pool instance); owned here and handed to each
     * pool model.
     */
    readonly #inventoryRowsPerPage = signal<InventoryRowsPerPage>(25);
    readonly #changeLogRowsPerPage = signal<InventoryRowsPerPage>(25);

    #constructPoolModel(poolId: string): void {
        this.#poolModel.value = new PoolModelImpl({
            poolId,
            queryObserverModelFactory: this.#queryObserverModelFactory,
            queryClient: this.#queryClient,
            store: this.#store,
            pool: computed(() => this.pools.value.find((pool) => pool.id === poolId) ?? null),
            rawPool: computed(() => this.#poolsQuery.signal.value.data?.pools.find((p) => p.id === poolId)),
            isLegacyPool: computed(() => this.legacyPoolIds.value.has(poolId)),
            hasSuperuserSession: this.#hasSuperuserSession,
            userRoles: this.#activeUser.userRoles,
            inventoryRowsPerPage: this.#inventoryRowsPerPage,
            changeLogRowsPerPage: this.#changeLogRowsPerPage,
            onPoolsChanged: async () => {
                await invalidatePoolsQuery(this.#queryClient, this.#firmId.value ?? undefined);
                this.#markFresh();
            },
            onFresh: () => this.#markFresh(),
        });
    }

    #teardownPoolModel(): void {
        const poolModel = this.#poolModel.value;
        if (!poolModel) return;
        this.#poolModel.value = null;
        poolModel[Symbol.dispose]();
    }

    downloadPoolListCsv(): void {
        if (typeof document === 'undefined') return;
        const pools = this.filteredPools.value;
        if (pools.length === 0) return;
        // Firm-scoped views have no firm-name map; fall back to the scoped firm's own name.
        const firmId = this.#firmId.value;
        const firmName = this.#firmName.value;
        const firmNamesByFirmId = this.firmNamesByFirmId.value ?? (firmId && firmName ? { [firmId]: firmName } : null);
        const columnLabels = {
            id: t('admin::pool-management::pool-list-export-col-id'),
            name: t('admin::pool-management::pool-list-export-col-name'),
            description: t('admin::pool-management::pool-list-export-col-description'),
            firm: t('admin::pool-management::pool-list-export-col-firm'),
            status: t('admin::pool-management::pool-list-export-col-status'),
            permission: t('admin::pool-management::pool-list-export-col-permission'),
        } satisfies PoolListCsvColumnLabels;
        const csv = createPoolListCsv(pools, { columnLabels, firmNamesByFirmId });
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        triggerCsvDownload(csv, `pools_${yyyy}-${mm}-${dd}.csv`);
    }

    async refresh(): Promise<void> {
        const firmId = this.#firmId.value;
        await invalidatePoolsQuery(this.#queryClient, firmId ?? undefined);
        const poolId = this.#selectedPoolId.value;
        if (poolId) {
            await Promise.all([
                invalidatePoolItemsQuery(this.#queryClient, poolId),
                invalidatePoolChangeLogQuery(this.#queryClient, poolId),
            ]);
        }
        this.#markFresh();
    }

    [Symbol.dispose](): void {
        this.#teardownPoolModel();
        this.#disposableStack.dispose();
    }
}
